import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { Divider } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  allowedParameterTemplatesFilesExtensions,
  parameterTemplatesFilesMaxFileSize,
  parameterTemplatesFilesUnit,
} from '../application-version-fields/applicationVersionFields.util';
import { DeviceAppVersionCard } from '../device-app-version-card/DeviceAppVersionCard.ctrl';
import {
  SystemPackageVersionSelectField,
  SystemPackageVersionUploadTemplatesField,
} from '../system-package-version-fields/SystemPackageVersionFields.ctrl';
import type { SystemPackageVersionConfigureStepProps } from './systemPackageVersionConfigureStep.model';
import styles from './systemPackageVersionConfigureStep.module.scss';
import { useSystemPackageVersionConfigureStep } from './useSystemPackageVersionConfigureStep.vm';

export function SystemPackageVersionConfigureStep({
  control,
  existingTemplates,
  setFieldError,
  clearFieldError,
  getUploadingProgress,
  /**
   * Create a copy of this component to match application scenario, where this variable is false
   * Rename this component to be the system package version.
   */
}: SystemPackageVersionConfigureStepProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION, ALL_LNGS_NAMESPACES.COMMON]);
  const { inputOptions } = useSystemPackageVersionConfigureStep({
    existingTemplates,
  });

  const shouldDisplayExistingTemplates = !!inputOptions.existingTemplates && inputOptions.existingTemplates.length > 0;

  return (
    <DeviceAppVersionCard
      subtitle={shouldDisplayExistingTemplates ? t('subtitle.configureSection') : ''}
      title={t('title.configureSection')}
    >
      {shouldDisplayExistingTemplates && (
        <>
          <SystemPackageVersionSelectField
            control={control}
            label={t('input.existingTemplates.label')}
            name={'configure.existingTemplates'}
            options={inputOptions.existingTemplates}
            placeholder={t('input.existingTemplates.placeholder')}
          />
          <Divider />
        </>
      )}
      <CoreTypography customClassName={styles.sectionTitle} variant={CoreTypographyVariant.Title03Medium}>
        {t('subtitle.uploadParameterTemplates')}
      </CoreTypography>
      <SystemPackageVersionUploadTemplatesField
        clearFieldError={clearFieldError}
        control={control}
        fileExtensions={allowedParameterTemplatesFilesExtensions}
        maxFileSize={parameterTemplatesFilesMaxFileSize}
        setFieldError={setFieldError}
        unitFileSize={t(`byteSizeUnit.${parameterTemplatesFilesUnit.toLowerCase()}`, {
          ns: ALL_LNGS_NAMESPACES.COMMON,
        })}
        getUploadingProgress={getUploadingProgress}
      />
    </DeviceAppVersionCard>
  );
}
